import React, { useState, useEffect, useCallback } from 'react';
import ListLoadErrorBanner from '../../components/ui/ListLoadErrorBanner';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
import ValidatedInput from '../../components/ui/ValidatedInput';
import ValidatedTextarea from '../../components/ui/ValidatedTextarea';
import codeGeneratorService from '../../services/codeGeneratorService';
import ActionDropdown from '../../components/ui/ActionDropdown';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ActionButtonsRow from '../../components/ui/ActionButtonsRow';
import SearchFilterCard, { FilterConfig } from '../../components/ui/SearchFilterCard';
import BulkActionsBar from '../../components/ui/BulkActionsBar';
import { RiEditLine, RiDeleteBinLine, RiEyeLine, RiDeleteBin2Line, RiFileDownloadLine, RiErrorWarningLine, RiStackLine, RiInformationLine, RiHistoryLine } from 'react-icons/ri';
import { showAlert, showConfirm } from '../../utils/alert';
import { mapFieldErrors, focusFirstInvalidField, getApiErrorMessageAsync } from '../../utils/mapFieldErrors';
import { useMutation } from '@tanstack/react-query';
import { queryClient } from '../../queryClient';
import deviceTypeService from '../../services/deviceTypeService';
import { DeviceType, CreateDeviceTypeRequest, UpdateDeviceTypeRequest } from '../../types/device';
import { PAGINATION_CONFIG } from '../../config/pagination';
import Pagination from '../../components/ui/Pagination';
import { useUserPageSize } from '../../hooks/useUserPageSize';
import { formatDateTimeFull, getLocalDateString } from '../../utils/dateUtils';
import { downloadExcelFile } from '../../utils/downloadExcel';
import { CanCreate, CanEdit, CanDelete } from '../../components/auth/PermissionGuard';
import { usePermission } from '../../hooks/usePermission';
import LoadingOverlayModal from '../../components/ui/LoadingOverlayModal';
import { useClampPage } from '../../hooks/useClampPage';
import { runBulkDelete, bulkDeleteFailureMessage } from '../../utils/bulkDelete';

import { useLatestRequest } from '../../hooks/useLatestRequest';
interface DeviceTypeForm {
  code: string;
  name: string;
  description: string;
  is_active: boolean;
}

interface FormErrors {
  code?: string;
  name?: string;
}

const DeviceTypePage: React.FC = () => {
  const { canView, canCreate, canEdit, canDelete, canExport } = usePermission();
  // S1118 URL-sync: read URL params (Variant A overwrite + filter synced to URL for deep-link)
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPageParam = searchParams.get('page');
  const urlStatusParam = searchParams.get('status') || '';

  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedDeviceType, setSelectedDeviceType] = useState<DeviceType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState<number>(() => {
    if (!urlPageParam) return 1;
    const n = parseInt(urlPageParam, 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(n, 9999);
  });
  const [totalPages, setTotalPages] = useState(1);
  const [formData, setFormData] = useState<DeviceTypeForm>({
    code: '',
    name: '',
    description: '',
    is_active: true
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [codeError, setCodeError] = useState<string>('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  // State สำหรับ sorting
  const [sortKey, setSortKey] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // State สำหรับควบคุมการแสดง success message
  const [shouldShowSuccess, setShouldShowSuccess] = useState(true);

  // State สำหรับ filter สถานะ (ส่งเป็น is_active param ให้ backend กรอง · synced to URL for deep-link UX)
  // S1118 URL-sync: hydrate filterStatus from URL
  const [filterStatus, setFilterStatus] = useState<string>(
    urlStatusParam === 'active' || urlStatusParam === 'inactive' ? urlStatusParam : ''
  );

  // S1118 URL-sync: write filter+page to URL (Variant A overwrite + simple-ref reset)
  const syncFiltersToUrl = useCallback((status: string, page: number) => {
    const newParams = new URLSearchParams();
    if (status === 'active' || status === 'inactive') newParams.set('status', status);
    if (page > 1) newParams.set('page', String(page));
    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    syncFiltersToUrl(filterStatus, currentPage);
  }, [filterStatus, currentPage, syncFiltersToUrl]);

  // S1118: filter→page reset (simple string ref)
  const prevFilterStatusRef = React.useRef(filterStatus);
  // BUG-S795 systemic (S796): กัน rapid double-click ยิง create mutate ซ้ำ same-tick (edit ใช้ optimistic-lock 409 อยู่แล้ว)
  const isSubmittingRef = React.useRef(false);
  useEffect(() => {
    if (prevFilterStatusRef.current !== filterStatus) {
      prevFilterStatusRef.current = filterStatus;
      setCurrentPage(1);
    }
  }, [filterStatus]);

  // Create mutation
  const createDeviceTypeMutation = useMutation({
    mutationFn: (data: CreateDeviceTypeRequest) => deviceTypeService.create(data),
    onSuccess: () => {
      if (shouldShowSuccess) {
        showAlert('สำเร็จ', 'เพิ่มประเภทอุปกรณ์สำเร็จ', 'success');
      }
      handleCloseModal();
      fetchDeviceTypes();
    },
    onError: async (error: any) => {
      console.error('Error creating device type:', error);

      // S1218 FIX (BUG-S1218-002): handle 422 field_errors inline FIRST · before string-match fallbacks
      const fieldErrs = mapFieldErrors(error);
      if (fieldErrs) {
        setFormErrors(fieldErrs);
        focusFirstInvalidField(fieldErrs);
        setShouldShowSuccess(false);
        return;
      }

      // ตรวจสอบว่าเป็น error รหัสซ้ำหรือไม่
      const errorMessage = error.response?.data?.error?.user_message ||
                           error.response?.data?.error?.message ||
                           error.response?.data?.detail || '';

      if (errorMessage.includes('ชื่อ') && errorMessage.includes('มีอยู่ในระบบแล้ว')) {
        // OBS-S1109-001 FIX (S1113): BUSINESS_RULE_VIOLATION on name dup → inline field error
        setFormErrors(prev => ({ ...prev, name: errorMessage }));
        setShouldShowSuccess(false);
        const nameEl = document.getElementsByName('name')[0] as HTMLInputElement | undefined;
        if (nameEl) nameEl.focus();
      } else if (errorMessage.includes('รหัสประเภทอุปกรณ์นี้มีอยู่แล้ว')) {
        // ถ้าเป็นรหัสซ้ำ ให้สร้างรหัสใหม่
        const oldCode = formData.code;
        const newCode = await codeGeneratorService.generateSingleCode('device_type');

        // อัพเดท state
        setFormData({...formData, code: newCode});

        // แสดงข้อความเตือน
        setCodeError(`รหัส ${oldCode} ไม่ว่างเนื่องจากมีการใช้งานแล้ว กรุณาบันทึกใหม่`);

        // ตั้งค่าให้ไม่แสดง success message
        setShouldShowSuccess(false);
      } else {
        // แสดงข้อความจริงจาก backend ถ้ามี (user bug Tirawat 2026-08-26: ข้อความกลางๆ ซ่อนสาเหตุ เช่น ซ้ำ)
        showAlert('เกิดข้อผิดพลาด', errorMessage || 'ไม่สามารถบันทึกข้อมูลได้', 'error');
      }
    }
  });

  const [itemsPerPage, setItemsPerPage] = useState(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  useUserPageSize(itemsPerPage, setItemsPerPage);
  const [totalItems, setTotalItems] = useState(0);
  useClampPage(currentPage, totalItems, itemsPerPage, setCurrentPage, loading);

  useEffect(() => {
    fetchDeviceTypes();
  }, [currentPage, searchTerm, itemsPerPage, filterStatus]);

  // Clear selection when filters or pagination change
  useEffect(() => {
    setSelectedIds([]);
  }, [currentPage, itemsPerPage, searchTerm, filterStatus]);

  const beginRequest = useLatestRequest();  // BUG-S2268-002: response ช้าทับของใหม่
  const fetchDeviceTypes = async () => {
    const isStale = beginRequest();
    setLoading(true);
    try {
      // BUG-S1520: กรอง is_active ที่ backend แทน client-side filter บนข้อมูล paginated
      // — เดิมจำนวนแถวที่แสดงไม่ตรงกับ totalItems จาก API + แถวที่ตรง filter ในหน้าอื่นไม่แสดง
      const response = await deviceTypeService.getAll({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        is_active: filterStatus === 'active' ? true : filterStatus === 'inactive' ? false : undefined
      });
      if (isStale()) return;
      
      
      // ตรวจสอบ structure ของ response
      setLoadError(null);
      if (response && Array.isArray(response.items)) {
        setDeviceTypes(response.items);
        setTotalPages(Math.ceil((response.total || 0) / itemsPerPage));
        setTotalItems(response.total || 0);
      } else if (Array.isArray(response)) {
        // ถ้า response เป็น array โดยตรง
        setDeviceTypes(response);
        setTotalPages(1);
        setTotalItems(response.length);
      } else {
        console.error('Unexpected response structure:', response);
        setDeviceTypes([]);
        setTotalPages(1);
        setTotalItems(0);
      }
    } catch (error) {
      if (isStale()) return;
      console.error('Error fetching device types:', error);
      setLoadError(error);
      showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดประเภทอุปกรณ์ได้', 'error');
      setDeviceTypes([]); // Set empty array on error
    } finally {
      if (!isStale()) setLoading(false);
    }
  };

  const handleOpenModal = async (mode: 'create' | 'edit' | 'view', deviceType?: DeviceType) => {
    setModalMode(mode);
    setSelectedDeviceType(deviceType || null);
    
    if (deviceType) {
      setFormData({
        code: deviceType.code,
        name: deviceType.name,
        description: deviceType.description || '',
        is_active: deviceType.is_active
      });
    } else {
      // ใช้ preview รหัสถัดไปแทนการสร้างรหัสจริง
      let previewCode = '';
      try {
        const nextCodePreview = await codeGeneratorService.getNextCodePreview('device_type', true);
        previewCode = nextCodePreview.next_code;
      } catch (error) {
        console.error('Error getting next code preview:', error);
        previewCode = ''; // fallback to empty
      }
      
      setFormData({
        code: previewCode,
        name: '',
        description: '',
        is_active: true
      });
    }
    
    setFormErrors({});
    setCodeError('');
    setShouldShowSuccess(true);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDeviceType(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      is_active: true
    });
    setFormErrors({});
    setCodeError('');
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.code.trim()) {
      errors.code = 'กรุณากรอกรหัสประเภทอุปกรณ์';
    }

    if (!formData.name.trim()) {
      errors.name = 'กรุณากรอกชื่อประเภทอุปกรณ์';
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      // Scroll to first error field
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (errorElement as HTMLInputElement).focus();
      }
      return false;
    }

    return true;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isSubmittingRef.current) return;  // BUG-S795: block same-tick 2nd submit while create in-flight

    if (!validateForm()) {
      return;
    }
    
    // ตั้งค่าให้แสดง success message เป็นค่าเริ่มต้น
    setShouldShowSuccess(true);
    
    if (modalMode === 'create') {
      const createData: CreateDeviceTypeRequest = {
        code: formData.code,
        name: formData.name,
        description: formData.description || undefined,
        is_active: formData.is_active
      };
      isSubmittingRef.current = true;
      createDeviceTypeMutation.mutate(createData, {
        onSettled: () => { isSubmittingRef.current = false; }
      });
    } else if (modalMode === 'edit' && selectedDeviceType) {
      const updateData: UpdateDeviceTypeRequest = {
        code: formData.code,
        name: formData.name,
        description: formData.description || undefined,
        is_active: formData.is_active
      };
      // KD3 S2112 (BUG-S2112-002): ดับเบิลคลิกบันทึกตอนแก้ไขยิง PUT 2 ครั้ง — ครั้งที่สองได้ 409
      // จาก If-Match ของครั้งแรก → ผู้ใช้เห็น "ข้อมูลถูกแก้ไขโดยผู้ใช้อื่น" ทั้งที่แก้คนเดียว
      // (ref กันกดซ้ำเดิมครอบเฉพาะเส้นสร้าง)
      isSubmittingRef.current = true;
      try {
        // BUG-S162-P1: send the record's last-seen timestamp so the backend can reject a
        // stale two-tab overwrite (optimistic concurrency → 409) instead of silently clobbering.
        const ifMatch = (selectedDeviceType as any).updated_at || (selectedDeviceType as any).created_at || undefined;
        await deviceTypeService.update(selectedDeviceType.id, updateData, ifMatch);
        showAlert('สำเร็จ', 'แก้ไขประเภทอุปกรณ์สำเร็จ', 'success');
        handleCloseModal();
        fetchDeviceTypes();
      } catch (error: any) {
        console.error('Error updating device type:', error);
        // BUG-S162-P1: optimistic-lock conflict — another tab/user saved first. Tell the
        // user to reload (don't silently overwrite their changes) and refresh the list.
        if (error.response?.status === 409) {
          showAlert(
            'ข้อมูลถูกแก้ไขโดยผู้ใช้อื่น',
            'มีผู้ใช้อื่นบันทึกข้อมูลนี้ไปแล้ว กรุณาปิดหน้าต่างนี้แล้วโหลดข้อมูลใหม่ก่อนแก้ไขอีกครั้ง',
            'error'
          );
          fetchDeviceTypes();
          return;
        }
        // S1218 FIX (BUG-S1218-002): handle 422 field_errors inline FIRST · before string-match fallbacks
        const fieldErrs = mapFieldErrors(error);
        if (fieldErrs) {
          setFormErrors(fieldErrs);
          focusFirstInvalidField(fieldErrs);
          return;
        }
        // OBS-S1109-001 FIX (S1113): BUSINESS_RULE_VIOLATION on name dup → inline field error (Edit flow)
        const rawMessage = error.response?.data?.error?.user_message ||
                           error.response?.data?.error?.message ||
                           error.response?.data?.detail || '';
        // detail ของ 422 อาจเป็น array → ใช้เฉพาะกรณีที่เป็นข้อความจริงเท่านั้น
        const errorMessage = typeof rawMessage === 'string' ? rawMessage : '';
        if (errorMessage.includes('ชื่อ') && errorMessage.includes('มีอยู่ในระบบแล้ว')) {
          setFormErrors(prev => ({ ...prev, name: errorMessage }));
          const nameEl = document.getElementsByName('name')[0] as HTMLInputElement | undefined;
          if (nameEl) nameEl.focus();
        } else if (error.response?.status === 404) {
          // BUG-S162-STALEEDIT: รายการถูกลบไปแล้ว (อีกแท็บ/อีกคน) ระหว่างที่ฟอร์มยังเปิดค้างอยู่
          // เดิมแจ้งแค่ "ไม่สามารถแก้ไขข้อมูลได้" + ค้างฟอร์มไว้ → ผู้ใช้กดบันทึกซ้ำกับ id ที่ไม่มีแล้วได้เรื่อยๆ
          // pattern เดียวกับ MaintenanceFormPage: บอกว่าถูกลบไปแล้ว + พาออกจากฟอร์ม + refresh รายการ
          showAlert('ไม่พบข้อมูล', errorMessage || 'ไม่พบประเภทอุปกรณ์นี้ อาจถูกลบไปแล้ว', 'error');
          handleCloseModal();
          fetchDeviceTypes();
        } else {
          showAlert('เกิดข้อผิดพลาด', errorMessage || 'ไม่สามารถแก้ไขข้อมูลได้', 'error');
        }
      } finally {
        isSubmittingRef.current = false;
      }
    }
  };

  const handleDelete = (deviceType: DeviceType) => {
    showConfirm(
      'ยืนยันการลบ',
      `คุณต้องการลบประเภทอุปกรณ์ "${deviceType.name}" ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้`,
      async () => {
        try {
          await deviceTypeService.delete(deviceType.id);
          showAlert('สำเร็จ', 'ลบประเภทอุปกรณ์สำเร็จ', 'success');
          fetchDeviceTypes();
        } catch (error: any) {
          console.error('Error deleting device type:', error);
          // ดึง error message จาก API response (รองรับหลายรูปแบบ)
          const errorMessage = error.response?.data?.error?.user_message ||
                               error.response?.data?.error?.message ||
                               error.response?.data?.detail ||
                               'เกิดข้อผิดพลาดในการลบข้อมูล';
          showAlert('เกิดข้อผิดพลาด', errorMessage, 'error');
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setFilterStatus('');
    setCurrentPage(1);
    setSelectedIds([]);
    setSortKey('created_at');
    setSortOrder('desc');
    fetchDeviceTypes();
  };

  // Handle column sorting
  const handleSort = (key: string, order: 'asc' | 'desc') => {
    setSortKey(key);
    setSortOrder(order);
  };

  // Sort data locally (การกรองสถานะทำที่ backend แล้ว — BUG-S1520)
  const sortedDeviceTypes = React.useMemo(() => {
    if (!deviceTypes || deviceTypes.length === 0) return [];

    return [...deviceTypes].sort((a, b) => {
      const aValue = a[sortKey as keyof DeviceType];
      const bValue = b[sortKey as keyof DeviceType];

      if (aValue === null || aValue === undefined) return sortOrder === 'asc' ? 1 : -1;
      if (bValue === null || bValue === undefined) return sortOrder === 'asc' ? -1 : 1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc'
          ? aValue.localeCompare(bValue, 'th')
          : bValue.localeCompare(aValue, 'th');
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [deviceTypes, sortKey, sortOrder]);

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      showAlert('เลือกข้อมูล', 'กรุณาเลือกประเภทอุปกรณ์ที่ต้องการลบ', 'info');
      return;
    }
    
    showConfirm(
      'ยืนยันการลบประเภทอุปกรณ์',
      `คุณต้องการลบประเภทอุปกรณ์ ${selectedIds.length} รายการที่เลือก ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้`,
      async () => {
        setBulkLoading(true);
        try {
          // BUG-S2103-002: allSettled — รายการที่ลบไม่ได้ต้องบอกเหตุผล · ตารางต้อง refetch · เลือกค้างเฉพาะที่ลบไม่ได้
          const result = await runBulkDelete(selectedIds, id => deviceTypeService.delete(id));
          setSelectedIds(result.failed.map(f => f.id));
          fetchDeviceTypes();
          if (result.failed.length === 0) {
            showAlert('สำเร็จ', `ลบประเภทอุปกรณ์ ${result.succeeded.length} รายการสำเร็จ`, 'success');
          } else {
            showAlert(result.succeeded.length ? 'ลบได้บางรายการ' : 'ลบไม่สำเร็จ', bulkDeleteFailureMessage('ประเภทอุปกรณ์', result), result.succeeded.length ? 'warning' : 'error');
          }
        } catch (error) {
          console.error('Error bulk deleting:', error);
          showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถลบรายการที่เลือกได้', 'error');
        } finally {
          setBulkLoading(false);
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  // State สำหรับ Export Loading
  const [isExporting, setIsExporting] = useState(false);

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      // ส่งตัวกรองที่กำลังใช้อยู่ไปด้วย — ไฟล์ต้องตรงกับที่เห็นบนจอ
      const blob = await deviceTypeService.exportToExcel({
        search: searchTerm || undefined,
        is_active: filterStatus === 'active' ? true : filterStatus === 'inactive' ? false : undefined,
      });
      downloadExcelFile(blob, `device-types-${getLocalDateString()}.xlsx`);
      showAlert('สำเร็จ', 'ส่งออกข้อมูลเรียบร้อยแล้ว', 'success');
    } catch (error) {
      console.error('Error exporting data:', error);
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถส่งออกข้อมูลได้'), 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(deviceTypes.map(item => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectItem = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(itemId => itemId !== id));
    }
  };

  // จัดการการเปลี่ยนแปลงข้อมูลในฟอร์ม
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
    
    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const columns = [
    {
      key: 'select',
      title: (
        <div className="flex items-center">
          <input
            type="checkbox"
            aria-label="เลือกทั้งหมด"
            checked={deviceTypes && selectedIds.length === deviceTypes.length && deviceTypes.length > 0}
            ref={(input) => { if (input) { input.indeterminate = selectedIds.length > 0 && !!deviceTypes && selectedIds.length < deviceTypes.length; } }}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
          />
        </div>
      ),
      width: '50px',
      render: (value: any, record: DeviceType) => (
        <input
          type="checkbox"
          aria-label={`เลือกรายการ ${record.code || record.id}`}
          checked={selectedIds.includes(record.id)}
          onChange={(e) => handleSelectItem(record.id, e.target.checked)}
          className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
        />
      ),
    },
    {
      key: 'code',
      title: 'รหัส',
      render: (value: string) => <span className="font-mono text-sm">{value || '-'}</span>,
      sortable: true,
      width: '120px'
    },
    {
      key: 'name',
      title: 'ชื่อประเภทอุปกรณ์',
      render: (value: string) => <span className="font-medium">{value || '-'}</span>,
      sortable: true
    },
    {
      key: 'description',
      title: 'คำอธิบาย',
      render: (value: string) => <span className="text-secondary-600">{value || '-'}</span>
    },
    {
      key: 'is_active',
      title: 'สถานะ',
      render: (value: boolean) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          value
            ? 'bg-success-100 text-success-800'
            : 'bg-danger-100 text-danger-800'
        }`}>
          {value ? 'ใช้งาน' : 'ไม่ใช้งาน'}
        </span>
      ),
      width: '100px'
    },
    {
      key: 'created_at',
      title: 'วันที่สร้าง',
      sortable: true,
      render: (value: string | null) => formatDateTimeFull(value)
    },
    {
      key: 'updated_at',
      title: 'อัพเดทล่าสุด',
      sortable: true,
      render: (value: string | null) => formatDateTimeFull(value)
    },
    {
      key: 'actions',
      title: 'การดำเนินการ',
      sticky: true,
      render: (_: any, record: DeviceType) => (
        <ActionDropdown
          actions={[
            ...(canView('device_types') ? [{
              label: 'ดูรายละเอียด',
              icon: <RiEyeLine className="w-4 h-4" />,
              onClick: () => handleOpenModal('view', record)
            }] : []),
            ...(canEdit('device_types') ? [{
              label: 'แก้ไข',
              icon: <RiEditLine className="w-4 h-4" />,
              onClick: () => handleOpenModal('edit', record)
            }] : []),
            ...(canDelete('device_types') ? [{
              divider: true
            },
            {
              label: 'ลบ',
              icon: <RiDeleteBinLine className="w-4 h-4" />,
              onClick: () => handleDelete(record),
              className: 'text-danger-600 hover:text-danger-700 hover:bg-danger-50'
            }] : [])
          ]}
          buttonIcon={null}
          buttonLabel=""
        />
      ),
      width: '60px'
    }
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Filter configuration สำหรับ SearchFilterCard
  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'สถานะ',
      type: 'select',
      options: [
        { value: 'active', label: 'ใช้งาน' },
        { value: 'inactive', label: 'ไม่ใช้งาน' },
      ],
      tagColor: 'success',
    },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header with Executive Navy Theme */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <RiStackLine className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-secondary-900 leading-tight">ประเภทอุปกรณ์</h1>
            <p className="text-xs text-secondary-500 mt-0.5">จัดการประเภทอุปกรณ์ในระบบ</p>
          </div>
        </div>
        {totalItems > 0 && (
          <span className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 rounded-lg bg-secondary-100 text-secondary-600 text-xs font-medium self-start sm:self-auto">
            {totalItems.toLocaleString()} รายการ
          </span>
        )}
      </div>

        <ListLoadErrorBanner error={loadError} entityLabel="ประเภทอุปกรณ์" onRetry={() => fetchDeviceTypes()} />

        {/* Action Buttons Row */}
        <ActionButtonsRow
          onAdd={canCreate('device_types') ? () => handleOpenModal('create') : undefined}
          onRefresh={handleRefresh}
          refreshLoading={loading}
          onExport={canExport('device_types') ? handleExportExcel : undefined}
          exportDisabled={!deviceTypes?.length}
        />

        {/* Search and Filter Card */}
        <SearchFilterCard
          searchTerm={searchTerm}
          onSearchChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          filters={filters}
          filterValues={{ status: filterStatus }}
          onFilterChange={(key, value) => {
            if (key === 'status') {
              setFilterStatus(value);
              setCurrentPage(1);
            }
          }}
          searchPlaceholder="ค้นหารหัสหรือชื่อประเภท..."
        />

        {/* Bulk Actions Bar */}
        {canDelete('device_types') && (
          <BulkActionsBar
            selectedCount={selectedIds.length}
            onBulkDelete={handleBulkDelete}
            onClearSelection={() => setSelectedIds([])}
            loading={bulkLoading}
          />
        )}

        {/* Table */}
        <Table
          columns={canDelete('device_types') ? columns : columns.filter(c => c.key !== 'select')}  /* KD3 S2122 (BUG-S2122-004): ไม่มีสิทธิ์ลบ = ไม่มีช่องติ๊ก (ติ๊กแล้วไม่มีอะไรให้ทำ) */
          data={sortedDeviceTypes}
          loading={loading}
          emptyText="ไม่พบข้อมูลประเภทอุปกรณ์"
          pagination={false}
          searchable={false}
          actions={false}
          rowKey="id"
          sortable={true}
          defaultSortKey={sortKey}
          defaultSortOrder={sortOrder}
          onSort={handleSort}
        />

        {/* Pagination */}
        {!loading && deviceTypes.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            pageSize={itemsPerPage}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newPageSize) => {
              setItemsPerPage(newPageSize);
              setCurrentPage(1);
            }}
            showPageSizeSelector={true}
            showSummary={true}
            className="mt-5"
          />
        )}

        {/* Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={
            modalMode === 'create' ? 'เพิ่มประเภทอุปกรณ์' :
            modalMode === 'edit' ? 'แก้ไขประเภทอุปกรณ์' : 'ดูรายละเอียดประเภทอุปกรณ์'
          }
          size="lg"
        >
          <div className="space-y-4">
            {/* Section: ข้อมูลพื้นฐาน */}
            <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-sm">
                <RiInformationLine className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-base font-semibold text-secondary-800">ข้อมูลพื้นฐาน</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* รหัสประเภทอุปกรณ์ - readonly ในทุกโหมด */}
              <div>
                <ValidatedInput
                  label="รหัสประเภทอุปกรณ์"
                  name="code"
                  value={formData.code}
                  onChange={handleFormChange}
                  error={formErrors.code}
                  required={true}
                  disabled={true}
                  placeholder="รหัสจะสร้างอัตโนมัติ"
                />
                {codeError && (
                  <p className="mt-1 text-sm text-danger-600 flex items-center">
                    <RiErrorWarningLine className="w-4 h-4 mr-1" />
                    {codeError}
                  </p>
                )}
              </div>
              <ValidatedInput
                label="ชื่อประเภทอุปกรณ์"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                error={formErrors.name}
                required={true}
                disabled={(modalMode as string) === 'view'}
                placeholder="เช่น โทรศัพท์มือถือ, แท็บเล็ต"
              />
            </div>

            <ValidatedTextarea
              label="คำอธิบาย"
              name="description"
              value={formData.description}
              onChange={handleFormChange}
              disabled={(modalMode as string) === 'view'}
              rows={3}
              placeholder="คำอธิบายเพิ่มเติมเกี่ยวกับประเภทอุปกรณ์"
            />
            
            {/* ฟิลด์สถานะ */}
            {modalMode !== 'view' && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  สถานะ
                </label>
                <select
                  aria-label="สถานะ"
                  name="is_active"
                  value={formData.is_active ? 'true' : 'false'}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                  disabled={(modalMode as string) === 'view'}
                >
                  <option value="true">ใช้งาน</option>
                  <option value="false">ไม่ใช้งาน</option>
                </select>
              </div>
            )}

            {modalMode === 'view' && selectedDeviceType && (
              <>
                {/* สถานะ */}
                <div className="border-t pt-4 mt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-secondary-600">สถานะ:</span>
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                        selectedDeviceType.is_active
                          ? 'bg-success-100 text-success-800'
                          : 'bg-danger-100 text-danger-800'
                      }`}>
                        {selectedDeviceType.is_active ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* ข้อมูลการติดตามผู้ใช้ */}
                <div className="mt-6 p-4 border border-secondary-200 rounded-lg bg-secondary-50">
                  {/* Section Header with Icon Box Pattern */}
                  <div className="flex items-center gap-2 pb-2 mb-4 border-b border-secondary-200">
                    <div className="w-8 h-8 bg-gradient-to-br from-info-500 to-info-600 rounded-lg flex items-center justify-center shadow-sm">
                      <RiHistoryLine className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-base font-semibold text-secondary-800">ข้อมูลการสร้างและแก้ไข</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-secondary-600">สร้างโดย:</span>
                      <span className="ml-2 font-medium">{selectedDeviceType.created_by?.full_name || selectedDeviceType.created_by?.username || '-'}</span>
                    </div>
                    <div>
                      <span className="text-secondary-600">วันที่สร้าง:</span>
                      <span className="ml-2 font-medium">
                        {formatDateTimeFull(selectedDeviceType.created_at)}
                      </span>
                    </div>
                    {/* แสดงข้อมูลการแก้ไขเฉพาะเมื่อเคยถูกแก้ไขจริง */}
                    {selectedDeviceType.updated_at && selectedDeviceType.updated_by && (
                      <>
                        <div>
                          <span className="text-secondary-600">แก้ไขล่าสุดโดย:</span>
                          <span className="ml-2 font-medium">{selectedDeviceType.updated_by?.full_name || selectedDeviceType.updated_by?.username || '-'}</span>
                        </div>
                        <div>
                          <span className="text-secondary-600">วันที่แก้ไขล่าสุด:</span>
                          <span className="ml-2 font-medium">
                            {formatDateTimeFull(selectedDeviceType.updated_at)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex flex-wrap justify-end gap-3 mt-6">
            <button
              onClick={handleCloseModal}
              className="px-4 py-2 text-secondary-700 bg-secondary-200 rounded-lg hover:bg-secondary-300"
            >
              {modalMode === 'view' ? 'ปิด' : 'ยกเลิก'}
            </button>
            {modalMode !== 'view' && (
              <button
                onClick={handleSubmit}
                disabled={createDeviceTypeMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {createDeviceTypeMutation.isPending ? 'กำลังบันทึก...' : modalMode === 'create' ? 'เพิ่ม' : 'บันทึก'}
              </button>
            )}
          </div>
        </Modal>

        {/* Loading Overlay for Export */}
        <LoadingOverlayModal
          isOpen={isExporting}
          icon="export"
          title="กำลังส่งออกข้อมูล"
          submessage="กรุณารอสักครู่ ระบบกำลังเตรียมไฟล์ Excel"
        />
      </div>
    </Layout>
  );
};

export default DeviceTypePage;